import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Loader2, RefreshCw, Database, Search, Sparkles, Play } from "lucide-react";
import { PreviewProspectCard, UnverifiedNote } from "./preview/PreviewProspectCard";
import { PreviewBlocked } from "./preview/PreviewBlocked";
import { PreviewGuidance } from "./preview/PreviewGuidance";

export { PreviewProspectCard as ProspectCard };

const Stat = ({ icon: Icon, text, testId }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400" data-testid={testId}><Icon className="h-3 w-3 text-ism-teal" />{text}</span>
);

const RunStats = ({ run, spec }) => {
  if (!run) return null;
  const inds = (spec?.industries || []).slice(0, 2).join(", ");
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-1" data-testid="preview-run-stats">
      {run.marketSize ? <Stat icon={Database} text={`~${Number(run.marketSize).toLocaleString()} companies match${inds ? ` in ${inds}` : ""}`} testId="preview-market-size" /> : null}
      <Stat icon={Search} text={`${run.companies || 0} firms · ${run.anchored || 0} people checked`} testId="preview-checked" />
      {run.sourceStatus?.companies?.ok && <Stat icon={Sparkles} text="Every firm verified in a company database" testId="preview-verified-note" />}
    </div>
  );
};

export const StepPreview = ({ data, job, busy, onBack, onNext, onRetry, onRetryUnblocked, onApplyGuidance }) => {
  const preview = data.preview;
  const prospects = preview?.prospects || [];
  const finished = !!preview && !job;
  const blocked = finished && preview.blocked;
  const thin = finished && !blocked && prospects.length < 3;
  const idle = !preview && !job && !busy;   // resumed from a saved draft: the preview itself is never stored, so offer to run it

  return (
    <div className="space-y-5" data-testid="onboarding-step-preview">
      <div>
        <h2 className="text-2xl text-white font-light">Does this look like your buyer?</h2>
        <p className="text-gray-400 mt-1 text-sm">A real hunt with your targeting — every firm checked against your industries and size, every person against your personas. If these are wrong, fix it now; it&apos;s cheaper than after 100 emails.</p>
      </div>

      {idle && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center space-y-3" data-testid="preview-idle">
          <p className="text-gray-300 text-sm">Welcome back — the preview hunt isn&apos;t saved between sessions. Run it again to see live prospects for your current targeting.</p>
          <Button onClick={onRetry} className="bg-ism-teal hover:bg-ism-teal/90 text-black rounded-lg" data-testid="preview-run-btn"><Play className="h-4 w-4 mr-1" /> Run the preview hunt</Button>
        </div>
      )}

      {job && (
        <div className="rounded-xl border border-ism-teal/20 bg-ism-teal/5 p-6 text-center" data-testid="preview-progress">
          <Loader2 className="h-8 w-8 animate-spin text-ism-teal mx-auto mb-3" />
          <p className="text-white text-sm" data-testid="preview-progress-stage">{job.stage}…</p>
          <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-ism-teal transition-all duration-500" style={{ width: `${job.progress}%` }} /></div>
        </div>
      )}

      {blocked && <PreviewBlocked blocked={preview.blocked} busy={busy} onRetry={onRetry} onContinueAnyway={onRetryUnblocked} />}

      {finished && !blocked && <RunStats run={preview.run} spec={data.spec} />}

      {finished && !blocked && prospects.length > 0 && (
        <div className="space-y-3" data-testid="preview-prospect-list">
          {prospects.map((p, i) => <PreviewProspectCard key={p.url || p.companyName || i} p={p} idx={i} spec={data.spec} />)}
          <UnverifiedNote prospects={prospects} />
        </div>
      )}

      {thin && preview.guidance && <PreviewGuidance guidance={preview.guidance} run={preview.run} busy={busy} onApply={onApplyGuidance} />}

      {thin && !preview.guidance && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5 text-sm" data-testid="preview-empty">
          <p className="text-amber-300">{prospects.length ? "Only a few verified matches on the first pass." : "No verified matches on the first pass."}</p>
          <p className="text-gray-400 mt-1">Usually the targeting is too narrow (size or geography) or the search terms don&apos;t match how your buyers describe themselves.</p>
          <Button variant="ghost" size="sm" onClick={onRetry} disabled={busy} className="text-ism-teal mt-2" data-testid="preview-retry-btn"><RefreshCw className="h-4 w-4 mr-1" /> Try again</Button>
        </div>
      )}

      <div className="flex justify-between flex-wrap gap-2 pt-2">
        <Button variant="ghost" onClick={onBack} disabled={busy} className="text-gray-400 hover:text-white" data-testid="onboarding-back-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Adjust targeting</Button>
        <Button onClick={onNext} disabled={busy || !finished} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium rounded-xl px-6" data-testid="onboarding-preview-next">{prospects.length ? "Yes, that's my buyer" : "Continue anyway"} <ArrowRight className="h-4 w-4 ml-2" /></Button>
      </div>
    </div>
  );
};
